import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import { Title } from '../../styled components/Typography';
import {
  CardAnnotationText,
  CardBullet,
  CardContentContainer,
  CardExpandButtonContainer,
  CardHeader,
  CardHeaderActionContainer,
  CardHeaderButton,
  StyledCard,
  StyledLockButton,
  CardUIBar
} from './Card.style';
import { UIBarGrowItem } from '../../styled components/UIBar';
import NavigationHistory from '../../libs/NavigationHistory';

/**
 * The Card component represents a title with expandable content,
 * on which some actions can be defined.
 *
 * An additional text annotation can be passed via the `annotation` property, its style can be altered via the `annotationCSSClasses` prop.
 *
 * Via the `greyedOut` and `canToggle` flags the card can be shown as disabled and/or without the option to open the content.
 *
 * The `showBullet` flag can be used to indicate that this card has for example new content.
 *
 * The browser's current URL can be altered when opening/closing the card's content
 * via the `urlToOpen` and `urlToClose` string properties.
 */

export default class Card extends React.Component {
  constructor(props) {
    super(props);
    this.state = { visible: props.visible };
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
  }

  handleTriggerClick() {
    const {
      canToggle,
      toggleCardCallback,
      navigationHistory,
      urlToOpen,
      urlToClose,
      replaceURL
    } = this.props;
    const { visible } = this.state;
    if (canToggle) {
      this.setState({ visible: !visible }, () => {
        if (toggleCardCallback) toggleCardCallback(!visible);
        navigationHistory.goTo(!visible ? urlToOpen : urlToClose, replaceURL);
      });
    }
  }

  renderCardHeaderExpandButton() {
    const { visible } = this.state;
    return (
      <CardExpandButtonContainer $rotated={!visible}>
        <Button
          contentType="icon"
          size="small"
          icon="expand-more"
          onClick={this.handleTriggerClick}
        />
      </CardExpandButtonContainer>
    );
  }

  renderCardHeader() {
    const { editFunction, hasLock, annotation, title, annotationColoring } = this.props;
    const hasPencilOrLock = editFunction || hasLock;
    return (
      <CardHeader data-test-card-header>
        {this.renderCardHeaderExpandButton()}
        <CardUIBar $hasPencilOrLock={hasPencilOrLock}>
          {/* TODO can the Button component be used here? */}
          <CardHeaderButton onClick={this.handleTriggerClick}>
            <Title as="h3" $truncate data-test-card-title>
              {title}
            </Title>
            <UIBarGrowItem />
            {annotation && (
              <CardAnnotationText data-test-type="annotation" $color={annotationColoring}>
                {annotation}
              </CardAnnotationText>
            )}
          </CardHeaderButton>
          {editFunction && (
            <CardHeaderActionContainer>
              <Button
                contentType="icon"
                size="small"
                icon="pencil"
                onClick={editFunction}
              />
            </CardHeaderActionContainer>
          )}
          {hasLock && (
            <CardHeaderActionContainer>
              <StyledLockButton />
            </CardHeaderActionContainer>
          )}
        </CardUIBar>
      </CardHeader>
    );
  }

  render() {
    const { greyedOut, showBullet, cardKey, children } = this.props;
    const { visible } = this.state;
    return (
      <StyledCard
        ref={(ref) => {
          this.htmlElement = ref;
        }}
        data-test-type="card"
        data-test-greyedout={greyedOut}
        $disabled={greyedOut}
        data-test-card-key={cardKey}
      >
        {showBullet && <CardBullet />}
        <div tw="relative">
          {this.renderCardHeader()}
          <CardContentContainer $visible={visible} data-test-contractedstate={!visible}>
            {children}
          </CardContentContainer>
        </div>
      </StyledCard>
    );
  }
}

Card.propTypes = {
  visible: PropTypes.bool,
  editFunction: PropTypes.func,
  hasLock: PropTypes.bool,
  canToggle: PropTypes.bool,
  annotation: PropTypes.string,
  title: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
    .isRequired,
  greyedOut: PropTypes.bool,
  showBullet: PropTypes.bool,
  toggleCardCallback: PropTypes.func,
  urlToOpen: PropTypes.string,
  urlToClose: PropTypes.string,
  replaceURL: PropTypes.bool,
  navigationHistory: PropTypes.instanceOf(NavigationHistory).isRequired,
  annotationColoring: PropTypes.string,
  /* Used to check the correct card when testing */
  cardKey: PropTypes.string
};

Card.defaultProps = {
  visible: false,
  canToggle: true,
  hasLock: false,
  annotation: null,
  editFunction: null,
  greyedOut: false,
  showBullet: false,
  toggleCardCallback: null,
  urlToOpen: '',
  urlToClose: '',
  replaceURL: false,
  annotationColoring: 'default',
  cardKey: ''
};
